import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { assignCaptainToTeam } from "../utils/rosterHelpers.js";
import TeamRoster from "./TeamRoster.js";
import TeamFans from "./TeamFans.js";
import { ScheduleTabs } from "./common/ScheduleTabs.js";
import FilterRadioPopover from "./common/FilterRadioPopover.js";
import { fetchData } from "../utils/api.js";
import { XIcon } from "./common/Icons.js";
import { Button } from "./ui/Button";
import EditIcon from "./common/EditIcon.js";

const TEAM_TABS = ["Roster", "Fans"];

const checkIsCaptainOrCoach = (user, team, captainInfo) => {
  if (!user || !team) return false;
  if (user.is_super_admin) return true;

  const userEmail = (user.email || "").toLowerCase();
  const captainEmail = (captainInfo?.email || "").toLowerCase();
  if (captainEmail && userEmail && captainEmail === userEmail) return true;

  if (
    captainInfo?.user_id &&
    user.user_id &&
    String(captainInfo.user_id) === String(user.user_id)
  ) {
    return true;
  }

  if (
    captainInfo?.firebase_id &&
    user.firebaseUid &&
    captainInfo.firebase_id === user.firebaseUid
  ) {
    return true;
  }

  if (
    team.coach_id &&
    (String(team.coach_id) === String(user.firebaseUid) ||
      String(team.coach_id) === String(user.id) ||
      String(team.coach_id) === String(user.uid))
  ) {
    return true;
  }

  const teamRole = (team.userRole || team.role || "").toLowerCase();
  return teamRole === "coach" || teamRole === "captain" || teamRole === "admin";
};

const TeamDetailView = ({
  team,
  user,
  teams = [],
  onSelectTeam = null,
  onOpenCreateTeam = null,
}) => {
  const [teamTab, setTeamTab] = useState("Roster");
  const [captainInfo, setCaptainInfo] = useState(null);
  const [editCaptainEmail, setEditCaptainEmail] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(team?.team_name || team?.name || "");
  const [editCity, setEditCity] = useState(team?.city || "");
  const [editState, setEditState] = useState(team?.state || "");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setEditName(team?.team_name || team?.name || "");
    setEditCity(team?.city || "");
    setEditState(team?.state || "");
  }, [team]);

  const isCoach = checkIsCaptainOrCoach(user, team, captainInfo);
  const canEditTeam = Boolean(user && isCoach);

  const fetchCaptainAndRoster = useCallback(async () => {
    const teamId = team?.id || team?.team_id;
    if (!teamId) return;
    try {
      const rosterCaptains = await fetchData(
        `/roster_details?team_id=eq.${teamId}&role=in.(captain,coach)`,
      );
      if (rosterCaptains && rosterCaptains.length > 0) {
        const cap = rosterCaptains[0];
        setCaptainInfo(cap);
        setEditCaptainEmail(cap.email || "");
      } else {
        setCaptainInfo(null);
        setEditCaptainEmail("");
      }
    } catch (e) {
      console.error("Failed to fetch team details", e);
    }
  }, [team?.id, team?.team_id]);

  useEffect(() => {
    void fetchCaptainAndRoster();
  }, [fetchCaptainAndRoster]);

  const handleSaveTeam = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const teamId = team.id || team.team_id;
      const payload = {
        team_name: editName,
        city: editCity,
        state: editState,
      };
      await fetchData(`/teams?team_id=eq.${teamId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const captainEmail = (captainInfo?.email || "").toLowerCase();
      const trimmedCaptainEmail = editCaptainEmail.trim().toLowerCase();
      if (trimmedCaptainEmail && trimmedCaptainEmail !== captainEmail) {
        await assignCaptainToTeam(teamId, trimmedCaptainEmail);
      }

      setIsEditing(false);
      await fetchCaptainAndRoster();
    } catch (error) {
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!team) return null;

  const currentTeamId = String(team.team_id || team.id || "");
  const teamOptionsList = teams.length > 0 ? teams : [team];
  const filterSections = [
    {
      id: "team",
      label: "Team",
      options: teamOptionsList.map((t) => ({
        value: String(t.team_id || t.id),
        label: t.team_name || t.name || "Unnamed Team",
      })),
      selectedValue: currentTeamId,
      onSelect: (nextId) => onSelectTeam?.(nextId),
    },
  ];

  return (
    <div className="team-detail-view single-layer-scroll">
      <div
        className="team-subsection-content"
        style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}
      >
        <div
          className="admin-row-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: "1.75rem",
              fontWeight: 600,
              lineHeight: "1.2",
            }}
          >
            <span className="detail-title-text">
              {team.team_name || team.name}
            </span>
          </h2>
          {canEditTeam && (
            <Button
              variant="ghost"
              size="icon"
              type="button"
              onClick={() => setIsEditing(true)}
              title="Edit Team"
            >
              <EditIcon size="sm" />
            </Button>
          )}
        </div>

        <div
          className="wireframe-subtab-bar"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <ScheduleTabs
              tabs={TEAM_TABS}
              activeTab={teamTab}
              onTabChange={setTeamTab}
            />
          </div>
          <div style={{ marginBottom: "20px" }}>
            <FilterRadioPopover
              sections={filterSections}
              footerAction={
                user && onOpenCreateTeam
                  ? { label: "Create Team", onClick: onOpenCreateTeam }
                  : null
              }
              ariaLabel="Team filters"
            />
          </div>
        </div>

        <div
          className="team-tab-content"
          style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}
        >
          {teamTab === "Roster" ? (
            <TeamRoster team={team} isCoach={canEditTeam} user={user} />
          ) : (
            <TeamFans team={team} isCoach={canEditTeam} user={user} />
          )}
        </div>
      </div>

      {isEditing && (
        <div className="drawer-overlay-container">
          <button
            type="button"
            className="drawer-overlay"
            onClick={() => setIsEditing(false)}
            aria-label="Close drawer"
          ></button>
          <dialog
            open
            className="drawer-content"
            aria-modal="true"
            aria-labelledby="drawer-title"
          >
            <div className="drawer-header">
              <h3 id="drawer-title" className="drawer-title">
                Edit Team Information
              </h3>
              <Button
                type="button"
                onClick={() => setIsEditing(false)}
                className="drawer-close-btn"
              >
                <XIcon size="sm" />
              </Button>
            </div>

            <form onSubmit={handleSaveTeam} className="form-compact">
              <div className="form-group full-width">
                <label htmlFor="drawerTeamName">Team Name</label>
                <input
                  id="drawerTeamName"
                  type="text"
                  className="input-base"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="e.g. Metro Valley Bombers"
                  required
                />
              </div>

              <div className="form-group full-width">
                <label htmlFor="drawerCaptainEmail">Captain Email</label>
                <input
                  id="drawerCaptainEmail"
                  type="email"
                  className="input-base"
                  value={editCaptainEmail}
                  onChange={(e) => setEditCaptainEmail(e.target.value)}
                  placeholder="e.g. captain@example.com"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="drawerTeamCity">City (Optional)</label>
                  <input
                    id="drawerTeamCity"
                    type="text"
                    className="input-base"
                    value={editCity}
                    onChange={(e) => setEditCity(e.target.value)}
                    placeholder="e.g. Seattle"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="drawerTeamState">State (Optional)</label>
                  <input
                    id="drawerTeamState"
                    type="text"
                    className="input-base"
                    maxLength="2"
                    value={editState}
                    onChange={(e) => setEditState(e.target.value.toUpperCase())}
                    placeholder="e.g. WA"
                  />
                </div>
              </div>

              <div className="modal-actions" style={{ marginTop: "1.5rem" }}>
                <div style={{ display: "flex", gap: "10px", width: "100%" }}>
                  <Button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    className="secondary-btn"
                    style={{ flex: 1 }}
                  >
                    Cancel
                  </Button>
                  <Button variant="primary" type="submit" disabled={isSaving}>
                    {isSaving ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              </div>
            </form>
          </dialog>
        </div>
      )}
    </div>
  );
};

TeamDetailView.propTypes = {
  team: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    team_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    coach_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    team_name: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    userRole: PropTypes.string,
    role: PropTypes.string,
  }),
  user: PropTypes.shape({
    id: PropTypes.string,
    uid: PropTypes.string,
    user_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    email: PropTypes.string,
    is_super_admin: PropTypes.bool,
    firebaseUid: PropTypes.string,
    getIdToken: PropTypes.func,
  }),
  teams: PropTypes.arrayOf(PropTypes.object),
  onSelectTeam: PropTypes.func,
  onOpenCreateTeam: PropTypes.func,
};

export default TeamDetailView;
